import React, { useState, useEffect } from "react";
import MasterTable from "../../../components/common/MasterTable";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import Loader from "../../../components/Loader/Loader";
import { format, isValid, parse } from "date-fns";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import DeliveryBoyModal from "../DeliveryBoyModal";
import DeliveryBoyDetailModal from "./DeliveryBoyDetailModal";
import DeliveryFilterModal from "./DeliveryFilterModal";
import FilterBar from "../../../components/common/FilterBar";

const DELIVERY_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "In-Active" },
];

const defaultFilterData = {
  fullName: "",
  mobileNo: "",
  status: [],
  search: "",
};

const DeliveryMaster = () => {
  const [pageSize, setPageSize] = useState(10);
  const [noData, setNoData] = useState(false);
  const [editData, setEditData] = useState({});
  const [data, setData] = useState([]);
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageDetails, setPageDetails] = useState({
    pageNo: 1,
    pageSize: 10,
    totalRecords: 3,
  });
  const loggedInUserName = sessionStorage.getItem("username");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [columnData, setColumnData] = useState("");

  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  useEffect(() => {
    fetchDeliveryBoyData(1, filterData);
  }, []);

  // fetch notification data
  const fetchDeliveryBoyData = async (
    page = currentPage,
    filters = filterData,
    size = pageSize
  ) => {
    setLoading(true);

    try {
      const requestBody = {
        username: loggedInUserName,
        fullName: filters?.fullName || "",
        mobileNo: filters?.mobileNo || "",
        search: filters?.search || "",
        // the backend takes one status; with several, the list below narrows them
        status: [].concat(filters?.status || []).length === 1 ? [].concat(filters.status)[0] : "",
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_DELIVERY_BOY}?&pageSize=${size}&pageNo=${page}`,
        requestBody
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        let modifiedData = modifyStructure(response?.data?.data);

        // Client-side filtering fallback for exact filtering
        if (filters?.fullName && modifiedData) {
          const filterName = filters.fullName.trim().toLowerCase();
          modifiedData = modifiedData.filter(
            (item) => (item?.fullName || "").trim().toLowerCase() === filterName
          );
        }

        if (filters?.mobileNo && modifiedData) {
          const filterMobile = filters.mobileNo.trim();
          modifiedData = modifiedData.filter(
            (item) => (item?.mobileNo || "").includes(filterMobile)
          );
        }

        const statusList = [].concat(filters?.status || []).filter(Boolean);
        if (statusList.length > 0 && modifiedData) {
          const upperStatuses = statusList.map((s) => s.toUpperCase());
          modifiedData = modifiedData.filter((item) =>
            upperStatuses.includes((item?.status || "").toUpperCase())
          );
        }

        setData(modifiedData || []);
        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setNoData(true);
        setData([]);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
      setNoData(true);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((user, index) => {
      let formattedCreDate = null;
      let formattedStrDate = null;

      if (user?.createdDate) {
        const parsed = parse(
          user.createdDate,
          "dd-MM-yyyy HH:mm:ss",
          new Date()
        );
        if (isValid(parsed)) {
          formattedCreDate = parsed;
        } else {
          console.warn("Invalid createdDate:", user.createdDate);
        }
      }

      if (user?.startDateTime) {
        const parsed = parse(
          user.startDateTime,
          "dd-MM-yyyy HH:mm:ss",
          new Date()
        );
        if (isValid(parsed)) {
          formattedStrDate = parsed;
        } else {
          console.warn("Invalid startDateTime:", user.startDateTime);
        }
      }

      const itemCount = user.itemsIncluded?.length || 0;

      const itemsIncludedIds = Array.isArray(user.itemsIncluded)
        ? user.itemsIncluded
        : []; // backend expects array

      return {
        ...user,

        itemsIncludedIds,

        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",

        startDateTime: formattedStrDate
          ? [
              format(formattedStrDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedStrDate, "HH:mm:ss"),
            ]
          : "",

        itemsIncluded: (
          // <span className="text-blue-600 underline cursor-pointer">
          <span>
            {itemCount > 0
              ? `${itemCount} Item${itemCount > 1 ? "s" : ""}`
              : "No Items"}
          </span>
        ),
      };
    });
  };

  const customerHeader = [
    "Full Name",
    "Contact Number",
    "Parent Type",
    "Parent Name",
    "Alternate Contact Number",
    "Email Address",
    "Status",
  ];

  const customerHeaderToKeyMap = {
    "Full Name": "fullName",
    "Contact Number": "mobileNo",
    "Parent Type": "parentType",
    "Parent Name": "parentName",
    "Alternate Contact Number": "alternateMobileNo",
    "Email Address": "email",
    Status: "status",
  };

  const handleAuthorization = (data, actionType = "view") => {
    console.log("::data::", data);
  };

  const handleAction = (data, actionType = "view") => {
    setModalAction(actionType);
    setColumnData(data);
    if (actionType === "add") {
      setShowModal(true);
    } else if (actionType === "modify") {
      setEditData(data);
      setShowModal(true);
    } else if (actionType === "acceptOtp") {
      setEditData(data);
      setDeliveryModalOpen(true);
    } else {
      setEditData(data);
      setShowModal(true);
    }
  };

  const handleDeliverySubmit = (selectedBoy) => {
    setDeliveryModalOpen(false);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchDeliveryBoyData(1, defaultFilterData, pageSize);
  };

  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Delivery Master" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchDeliveryBoyData(1, next, pageSize);
        }}
        search={{ key: "search", placeholder: "Search name, mobile, email or hub / LMBP code" }}
        selects={[
          {
            key: "status",
            allLabel: "All statuses",
            chipLabel: "Status",
            options: DELIVERY_STATUS_OPTIONS,
            toValue: (stored) => [].concat(stored || [])[0] || "",
            fromOption: (option) => (option ? [option.value] : []),
          },
        ]}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
          {loading ? (
            <Loader />
          ) : (
            <>
              <MasterTable
                loading={loading}
                headers={customerHeader}
                data={data}
                handleAction={handleAction}
                filterFields={["fullName"]}
                entityType="Delivery"
                headerToKeyMap={customerHeaderToKeyMap}
                handleAuth={handleAuthorization}
                noData={noData}
                pageDetails={pageDetails}
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecord={totalRecord}
                fetchNextData={(page, filters = filterData, size = pageSize) =>
                  fetchDeliveryBoyData(page, filters, size)
                }
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                openModalFilter={openModalFilter}
                handleReset={handleReset}
                fetchData={() =>
                  fetchDeliveryBoyData(1, defaultFilterData, pageSize)
                }
                filterData={filterData}
                hideFilterControls
              />
            </>
          )}

          {showModal && (
            <DeliveryBoyDetailModal
              show={showModal}
              handleClose={() => setShowModal(false)}
              editData={editData}
              action={modalAction}
              callApi={fetchDeliveryBoyData}
            />
          )}

          {deliveryModalOpen && (
            <DeliveryBoyModal
              open={deliveryModalOpen}
              onClose={() => setDeliveryModalOpen(false)}
              onSubmit={handleDeliverySubmit}
            />
          )}

          {isModalOpenFilter && (
            <DeliveryFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              setData={setData}
              setPageDetails={setPageDetails}
              setNoData={setNoData}
              pageSize={pageSize}
              currentPage={currentPage}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              deliveryList={data}
              fetchDeliveryBoyData={fetchDeliveryBoyData}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default DeliveryMaster;
